import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Menu, ArrowRight, LogIn, Loader2, MessageCircle, Mail, Wallet, LogOut, User } from 'lucide-react';
import { SEOHead } from '../components/seo/SEOHead';
import { NightClubSchema, WebSiteSchema } from '../components/seo/SchemaMarkup';
import { MarqueeBand } from '../components/campaign/MarqueeBand';
import { StickerBadge } from '../components/campaign/StickerBadge';
import { Logo } from '../components/Logo';
import { publicApi, customerApi } from '../lib/api';
import { toast } from 'sonner';
import { useCustomerAuth } from '../App';
import { useWalletBalance } from '../hooks/useWalletQueries';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import { 
  FeaturedEventSection, 
  UpcomingEventsGrid, 
  ExperienceSection, 
  VIPSection, 
} from './HomepageCampaignSections';
import {
  WalletHero,
  EditorialGallery,
  PromoterTeaser,
  ReservationStripFooter,
} from '../components/home/HomeRedesignSections';
import Footer from '../components/Footer';
import { blogPosts as _blogPostsData } from '../data/blogPosts';
import { Badge } from '../components/ui/badge';

export default function HomepageCampaign() {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const navigate = useNavigate();
  const { loginCustomer, customer, logoutCustomer } = useCustomerAuth();

  // Wallet — only fetched when a customer is logged in
  const { data: walletBalance } = useWalletBalance({ enabled: !!customer });

  // Login state
  const [loginStep, setLoginStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpChannel, setOtpChannel] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [countdown, setCountdown] = useState(35);
  const [canShowFallback, setCanShowFallback] = useState(false);

  useEffect(() => {
    if (loginStep === 2 && otpChannel === 'whatsapp' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      setCanShowFallback(true);
    }
  }, [loginStep, otpChannel, countdown]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    const cleaned = phone.replace(/\s/g, '');
    if (!/^[6-9]\d{9}$/.test(cleaned)) {
      toast.error('Enter a valid 10-digit mobile number');
      return;
    }
    setSendingOtp(true);
    try {
      const response = await publicApi.sendOTPSmart('+91' + cleaned);
      setLoginStep(2);
      setOtpChannel(response.data.channel);
      setCountdown(35);
      setCanShowFallback(false);
      toast.success(response.data.channel === 'whatsapp' ? '🎉 OTP sent to WhatsApp!' : '📱 OTP sent via SMS');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error('Enter a valid 6-digit OTP');
      return;
    }
    setVerifying(true);
    try {
      const phoneE164 = '+91' + phone.replace(/\s/g, '');
      const res = await customerApi.loginOTP(phoneE164, otp);
      // res.data: { access_token, token_type, customer }
      loginCustomer(res.data.access_token, res.data.customer);
      toast.success(`✅ Welcome back, ${res.data.customer.full_name || ''}!`.trim());
      setShowLoginModal(false);
      setTimeout(() => navigate('/account'), 400);
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || 'Invalid OTP';
      if (status === 404) {
        // No customer account — redirect to signup
        toast.error('No account found for this number. Please sign up.');
        setShowLoginModal(false);
        setTimeout(() => navigate('/account/signup'), 500);
      } else {
        toast.error(detail);
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleSMSFallback = async () => {
    try {
      await publicApi.sendOTPSMSFallback('+91' + phone.replace(/\s/g, ''));
      setOtpChannel('sms');
      setCountdown(35);
      setCanShowFallback(false);
      toast.success('📱 OTP sent via SMS');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const resetLogin = () => {
    setLoginStep(1);
    setPhone('');
    setOtp('');
    setOtpChannel('');
    setCountdown(35);
    setCanShowFallback(false);
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] relative">
      {/* SEO Meta Tags */}
      <SEOHead
        title="Scratch Unleashed - Best Night Club in Jamshedpur | Premium Nightlife"
        description="Experience premium nightlife at Scratch Unleashed, Jamshedpur's best night club. World-class DJs, VIP tables, DJ nights every weekend. Located near Sonari & Bindal Mall."
        keywords="night club Jamshedpur, best night club Jamshedpur, DJ night Jamshedpur, party place Jamshedpur, VIP table booking Jamshedpur, nightlife Jamshedpur, clubs near Sonari, Bindal Mall nightlife"
        canonical="https://scratchjsr.com/"
      />
      <NightClubSchema />
      <WebSiteSchema />

      {/* Grain Overlay */}
      <div className="grain-overlay"></div>

      {/* Navigation */}
      <nav data-testid="home-nav" className="fixed top-0 left-0 right-0 z-50 bg-[var(--surface)]/80 backdrop-blur-md border-b border-[var(--border-custom)]">
        <div className="max-w-[1120px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Logo variant="icon" height={40} className="cursor-pointer" onClick={() => navigate('/')} />

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/events" className="text-sm text-[var(--text-dim)] hover:text-white transition-colors duration-200">Events</Link>
            <a href="#vip" className="text-sm text-[var(--text-dim)] hover:text-white transition-colors duration-200">VIP</a>
            <a href="#gallery" className="text-sm text-[var(--text-dim)] hover:text-white transition-colors duration-200">Gallery</a>
            <Link to="/blog" data-testid="home-nav-blog-link" className="text-sm text-[var(--text-dim)] hover:text-white transition-colors duration-200">Blog</Link>

            {customer ? (
              <>
                {/* Wallet Pill (only when logged in) */}
                <button
                  type="button"
                  data-testid="home-nav-wallet-pill"
                  onClick={() => navigate('/account/wallet')}
                  className="group flex items-center gap-2 px-3 py-1.5 rounded-xl border border-[rgba(212,168,67,0.3)] bg-[rgba(212,168,67,0.08)] hover:bg-[rgba(212,168,67,0.14)] transition-colors"
                  title={
                    walletBalance?.expiring_soon
                      ? `${walletBalance.expiring_soon} pts expiring soon`
                      : 'Scratch Points wallet'
                  }
                >
                  <Wallet size={14} className="text-[#D4A843]" />
                  <span className="text-sm font-mono font-bold text-[#D4A843]">
                    {walletBalance?.available ?? 0}
                  </span>
                  <span className="text-[10px] text-[#B9B9B9] uppercase tracking-widest hidden lg:inline">pts</span>
                  {walletBalance?.expiring_soon > 0 && (
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF4D4D] animate-pulse" />
                  )}
                </button>

                {/* Customer avatar dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      data-testid="home-nav-customer-menu"
                      className="w-9 h-9 rounded-full bg-[rgba(255,255,255,0.06)] border border-[var(--border-custom)] hover:bg-[rgba(255,255,255,0.1)] flex items-center justify-center text-sm font-semibold text-[#F2F2F2]"
                      aria-label="Account menu"
                    >
                      {(customer.full_name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-[#0F0F0F] border-[rgba(255,255,255,0.1)] text-[#F2F2F2]">
                    <DropdownMenuLabel className="text-xs text-[#6F6F6F]">
                      Signed in as<br/>
                      <span className="text-[#F2F2F2] text-sm">{customer.full_name || customer.phone}</span>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-[rgba(255,255,255,0.07)]" />
                    <DropdownMenuItem onClick={() => navigate('/account')} className="cursor-pointer focus:bg-[rgba(255,255,255,0.05)]">
                      <User size={14} className="mr-2" /> Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/account/wallet')} className="cursor-pointer focus:bg-[rgba(255,255,255,0.05)]">
                      <Wallet size={14} className="mr-2" /> Scratch Points
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-[rgba(255,255,255,0.07)]" />
                    <DropdownMenuItem
                      onClick={() => { logoutCustomer(); toast.success('Signed out'); }}
                      className="cursor-pointer text-[#FF6B6B] focus:bg-[rgba(255,77,77,0.08)] focus:text-[#FF4D4D]"
                      data-testid="home-nav-logout"
                    >
                      <LogOut size={14} className="mr-2" /> Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                data-testid="home-nav-login-button"
                className="text-[var(--text-dim)] hover:text-white hover:bg-white/5 rounded-xl"
                onClick={() => setShowLoginModal(true)}
              >
                <LogIn size={16} className="mr-2" />
                Login
              </Button>
            )}

            <Button 
              variant="outline" 
              size="sm"
              data-testid="home-nav-reserve-button"
              className="border-[var(--border-custom)] text-white hover:bg-white/5 rounded-xl"
              onClick={() => navigate('/checkin')}
            >
              Reserve
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isNavOpen} onOpenChange={setIsNavOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="home-nav-menu-button">
                <Menu size={24} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[var(--surface)] border-l border-[var(--border-custom)] w-[280px]">
              <div className="flex flex-col gap-6 mt-8">
                <div className="space-y-4">
                  <Link to="/events" className="block text-lg" onClick={() => setIsNavOpen(false)}>Events</Link>
                  <a href="#vip" className="block text-lg" onClick={() => setIsNavOpen(false)}>VIP</a>
                  <a href="#gallery" className="block text-lg" onClick={() => setIsNavOpen(false)}>Gallery</a>
                  <Link to="/blog" className="block text-lg" onClick={() => setIsNavOpen(false)} data-testid="mobile-nav-blog-link">Blog &amp; Guides</Link>
                </div>
                <div className="pt-6 border-t border-[var(--border-custom)] space-y-3">
                  <Button className="w-full btn-crimson-glow rounded-xl" onClick={() => { setIsNavOpen(false); navigate('/checkin'); }}>
                    Reserve Now
                  </Button>
                  {customer ? (
                    <>
                      <Button
                        variant="outline"
                        className="w-full border-[rgba(212,168,67,0.35)] bg-[rgba(212,168,67,0.06)] text-[#D4A843] hover:bg-[rgba(212,168,67,0.12)] rounded-xl"
                        data-testid="home-nav-wallet-pill-mobile"
                        onClick={() => { setIsNavOpen(false); navigate('/account/wallet'); }}
                      >
                        <Wallet size={16} className="mr-2" />
                        {walletBalance?.available ?? 0} Scratch Points
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full border-[var(--border-custom)] text-white hover:bg-white/5 rounded-xl"
                        onClick={() => { setIsNavOpen(false); navigate('/account'); }}
                      >
                        <User size={16} className="mr-2" />
                        My Account
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="outline"
                      data-testid="home-nav-login-button-mobile"
                      className="w-full border-[var(--border-custom)] text-white hover:bg-white/5 rounded-xl"
                      onClick={() => { setIsNavOpen(false); setShowLoginModal(true); }}
                    >
                      <LogIn size={16} className="mr-2" />
                      Login
                    </Button>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>

      {/* Hero Section — Apple Wallet card + featured photo (redesigned) */}
      <WalletHero navigate={navigate} />

      {/* Marquee Band */}
      <MarqueeBand text="SCRATCH UNLEASHED • FRI-SUN 6PM-LATE • NO PHOTOS • DRESS CODE" />

      {/* Local SEO Content Block */}
      <LocalSEOBlock navigate={navigate} />

      {/* Featured Event Section */}
      <FeaturedEventSection navigate={navigate} />

      {/* Upcoming Events */}
      <UpcomingEventsGrid navigate={navigate} />

      {/* Experience Section */}
      <ExperienceSection />

      {/* VIP Section */}
      <VIPSection navigate={navigate} />

      {/* Gallery — editorial bento wall with new nightlife photos */}
      <EditorialGallery />

      {/* Promoter Program teaser */}
      <PromoterTeaser navigate={navigate} />

      {/* Latest from the Blog (3 featured guides, crawlable Link cards) */}
      <BlogTeaserSection />

      {/* Final CTA — Reservation strip footer */}
      <ReservationStripFooter navigate={navigate} />

      {/* Marquee Footer */}
      <MarqueeBand text="FRI-SUN 6PM-LATE • 21+ • DRESS CODE ENFORCED • TABLE RESERVATIONS • WEEKDAYS: PRIVATE EVENTS" className="border-t-0" />

      {/* Footer (address, hours, map link, JSON-LD) */}
      <Footer />

      {/* Mobile Sticky CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[var(--surface)] border-t border-[var(--border-custom)] p-3 flex gap-2">
        <Button className="flex-1 btn-crimson-glow rounded-xl" onClick={() => navigate('/checkin')}>
          Reserve Now
        </Button>
        <Button variant="outline" className="flex-1 border-[var(--border-custom)] rounded-xl" onClick={() => navigate('/checkin')}>
          Guest List
        </Button>
      </div>

      {/* Customer Login Modal */}
      <Dialog
        open={showLoginModal}
        onOpenChange={(open) => {
          setShowLoginModal(open);
          if (!open) resetLogin();
        }}
      >
        <DialogContent data-testid="login-modal" className="sm:max-w-[425px] bg-[#0A0A0A] border-[#2A2A2A]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-['EB_Garamond'] text-[#F2F2F2]">
              {loginStep === 1 ? 'Welcome Back' : 'Verify Your Number'}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#6F6F6F]">
              {loginStep === 1
                ? 'Sign in with your mobile number to continue.'
                : 'Enter the 6-digit code we just sent you.'}
            </DialogDescription>
          </DialogHeader>

          {loginStep === 1 ? (
            <form onSubmit={handleSendOTP} className="space-y-4 mt-4" data-testid="login-phone-form">
              <div>
                <Label htmlFor="login-phone" className="text-[#B9B9B9] text-sm">Mobile Number</Label>
                <div className="flex gap-2 mt-2">
                  <div className="flex items-center px-3 rounded-lg border border-[#2A2A2A] bg-[#111111]">
                    <span className="text-[#6F6F6F]">+91</span>
                  </div>
                  <Input
                    id="login-phone"
                    data-testid="login-phone-input"
                    type="tel"
                    placeholder="9876543210"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    maxLength={10}
                    className="flex-1 bg-[#111111] border-[#2A2A2A] text-[#F2F2F2]"
                    autoFocus
                  />
                </div>
              </div>
              <Button
                type="submit"
                data-testid="login-send-otp-button"
                className="w-full btn-gold"
                disabled={sendingOtp || phone.length !== 10}
              >
                {sendingOtp ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending OTP...</>
                ) : (
                  'Continue'
                )}
              </Button>
              <p className="text-xs text-center text-[#6F6F6F]">
                You'll receive an OTP via WhatsApp or SMS
              </p>
            </form>
          ) : (
            <div className="space-y-4 mt-4" data-testid="login-otp-step">
              <div
                className="p-3 rounded-lg"
                style={{
                  background: otpChannel === 'whatsapp' ? 'rgba(37,211,102,0.08)' : 'rgba(212,168,67,0.08)',
                  border: `1px solid ${otpChannel === 'whatsapp' ? 'rgba(37,211,102,0.25)' : 'rgba(212,168,67,0.25)'}`
                }}
              >
                <div className="flex items-center gap-2 text-sm">
                  {otpChannel === 'whatsapp' ? (
                    <>
                      <MessageCircle size={14} style={{ color: '#25D366' }} />
                      <span style={{ color: '#25D366' }}>OTP sent via WhatsApp</span>
                    </>
                  ) : (
                    <>
                      <Mail size={14} style={{ color: '#D4A843' }} />
                      <span style={{ color: '#D4A843' }}>OTP sent via SMS</span>
                    </>
                  )}
                </div>
                <p className="text-xs text-[#B9B9B9] mt-1">Sent to +91 {phone}</p>
              </div>

              <form onSubmit={handleVerifyOTP} className="space-y-4" data-testid="login-otp-form">
                <div>
                  <Label htmlFor="login-otp" className="text-[#B9B9B9] text-sm">Enter OTP</Label>
                  <Input
                    id="login-otp"
                    data-testid="login-otp-input"
                    type="text"
                    placeholder="000000"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    className="mt-2 bg-[#111111] border-[#2A2A2A] text-[#F2F2F2] text-center text-2xl tracking-widest"
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="login-verify-otp-button"
                  className="w-full btn-gold"
                  disabled={verifying || otp.length !== 6}
                >
                  {verifying ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying...</>
                  ) : (
                    'Login'
                  )}
                </Button>
              </form>

              {otpChannel === 'whatsapp' && !canShowFallback && (
                <p className="text-xs text-center text-[#6F6F6F]">SMS fallback in {countdown}s</p>
              )}

              {canShowFallback && (
                <Button
                  variant="outline"
                  data-testid="login-sms-fallback-button"
                  className="w-full border-[#2A2A2A] text-[#B9B9B9]"
                  onClick={handleSMSFallback}
                >
                  <Mail className="mr-2 h-4 w-4" /> Send via SMS instead
                </Button>
              )}

              <button
                type="button"
                data-testid="login-change-number-button"
                onClick={resetLogin}
                className="w-full text-sm text-[#6F6F6F] hover:text-[#B9B9B9] transition-colors"
              >
                Change number
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Local SEO Content Block Component
function LocalSEOBlock({ navigate }) {
  return (
    <section className="relative py-16 sm:py-20 bg-[var(--surface-2)] border-t border-b border-[var(--border)]">
      <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Main Content */}
          <div className="lg:col-span-8 space-y-6">
            <div>
              <h2 className="font-bebas text-3xl sm:text-4xl mb-4 tracking-tight">
                Jamshedpur's Premier Nightlife Destination
              </h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  Located in the heart of Jamshedpur near Sonari and Bindal Mall, <strong>Scratch Unleashed</strong> has redefined nightlife in the city. Since opening our doors, we've established ourselves as the go-to destination for anyone seeking an authentic, premium clubbing experience without leaving Jamshedpur. Whether you're a resident of Bistupur, Kadma, or Mango, we're easily accessible and worth the journey.
                </p>
                <p>
                  What makes us the <strong>best night club in Jamshedpur</strong>? It's our unwavering commitment to excellence across every element of the nightlife experience. From our world-class Funktion-One sound system to our carefully curated DJ lineup featuring both resident talent and guest performers from across India, we've built a reputation for delivering consistently exceptional nights. Our venue design combines atmospheric lighting, premium interiors, and multiple zones—including VIP lounges, a main dance floor, and a rooftop area—creating distinct experiences within one location.
                </p>
                <p>
                  Every Friday through Sunday from 6 PM onwards, we host <strong>DJ nights</strong> that draw crowds from across Jamshedpur and neighboring cities. Our programming spans house, techno, hip-hop, and Bollywood, ensuring there's something for every musical taste. For those seeking exclusive experiences, we're also available for private events Monday through Thursday. Beyond regular nights, Scratch Unleashed is Jamshedpur's premier venue for special celebrations—from birthday parties and corporate events to our legendary New Year's Eve countdown. With VIP table booking, bottle service, and customizable party packages, we cater to those who demand the best.
                </p>
                <p>
                  More than just a nightclub, Scratch Unleashed represents a cultural shift in Jamshedpur's entertainment landscape. We've proven that you don't need to travel to Mumbai, Bangalore, or Delhi for world-class nightlife—the experience is right here, in your city, waiting for you to discover it.
                </p>
              </div>
            </div>
          </div>

          {/* Quick Links Sidebar */}
          <div className="lg:col-span-4 space-y-4">
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-6">
              <h3 className="font-bebas text-2xl mb-4">Explore More</h3>
              <div className="space-y-3">
                <Link
                  to="/night-club-in-jamshedpur"
                  className="block w-full text-left px-4 py-3 rounded-xl bg-[var(--bg)] border border-[var(--border)] hover:border-[var(--accent-gold)] transition-colors duration-200 text-sm"
                >
                  <span className="text-[var(--accent-gold)]">→</span> Night Club in Jamshedpur
                </Link>
                <Link
                  to="/dj-night-jamshedpur"
                  className="block w-full text-left px-4 py-3 rounded-xl bg-[var(--bg)] border border-[var(--border)] hover:border-[var(--accent-gold)] transition-colors duration-200 text-sm"
                >
                  <span className="text-[var(--accent-gold)]">→</span> DJ Nights &amp; Events
                </Link>
                <Link
                  to="/vip-table-booking-jamshedpur"
                  className="block w-full text-left px-4 py-3 rounded-xl bg-[var(--bg)] border border-[var(--border)] hover:border-[var(--accent-gold)] transition-colors duration-200 text-sm"
                >
                  <span className="text-[var(--accent-gold)]">→</span> VIP Table Booking
                </Link>
                <Link
                  to="/birthday-party-venue-jamshedpur"
                  className="block w-full text-left px-4 py-3 rounded-xl bg-[var(--bg)] border border-[var(--border)] hover:border-[var(--accent-gold)] transition-colors duration-200 text-sm"
                >
                  <span className="text-[var(--accent-gold)]">→</span> Birthday Party Venue
                </Link>
                <Link
                  to="/new-year-party-jamshedpur"
                  className="block w-full text-left px-4 py-3 rounded-xl bg-[var(--bg)] border border-[var(--border)] hover:border-[var(--accent-gold)] transition-colors duration-200 text-sm"
                >
                  <span className="text-[var(--accent-gold)]">→</span> New Year Party 2026
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Bold Campaign Hero Component
function BoldCampaignHero({ navigate }) {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);

  return (
    <motion.section
      data-testid="home-hero"
      className="relative min-h-[100vh] flex items-center overflow-hidden pt-16 pb-24 md:pb-20"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      {/* Background Layers */}
      <div className="absolute inset-0 z-0 hero-campaign-gradient"></div>
      <div className="scan-lines"></div>
      <div className="light-rays"></div>

      {/* Massive Background Word with Parallax */}
      <motion.div 
        className="absolute top-[15%] left-1/2 -translate-x-1/2 z-0 w-full"
        style={{ y }}
      >
        <h2 className="outlined-giant-word font-bebas text-[140px] sm:text-[200px] lg:text-[280px] xl:text-[340px] text-center whitespace-nowrap">
          UNLEASHED
        </h2>
      </motion.div>

      {/* Main Content */}
      <div className="relative z-10 max-w-[1200px] mx-auto px-4 sm:px-6 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          
          {/* Left: Massive Overlapping Typography */}
          <div className="lg:col-span-7 space-y-6 relative">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h1 
                data-testid="home-hero-headline"
                className="poster-headline text-[72px] sm:text-[96px] lg:text-[120px] xl:text-[140px] leading-[0.82]"
              >
                WHERE
                <br />
                NIGHTS
                <br />
                <span className="text-[var(--accent-crimson)] glow-text-crimson">IGNITE</span>
              </h1>
            </motion.div>

            <motion.p 
              data-testid="home-hero-subhead"
              className="text-base md:text-lg text-[var(--text-dim)] max-w-[42ch] relative z-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              Underground sound. Premium spirits. No phones on the floor.
              <br />
              <span className="text-[var(--accent-gold)]">This is your escape.</span>
            </motion.p>

            <motion.div 
              className="flex flex-col sm:flex-row gap-3 pt-2 relative z-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <Button 
                data-testid="hero-reserve-button"
                className="btn-crimson-glow rounded-xl px-7 py-6 text-base font-semibold"
                onClick={() => navigate('/checkin')}
              >
                Reserve Your Table
                <ArrowRight size={18} className="ml-2" />
              </Button>
              <Button 
                variant="outline"
                className="rounded-xl px-7 py-6 text-base font-semibold border-[var(--border-custom)] hover:bg-white/5 bg-black/20 backdrop-blur-sm"
              >
                View This Week
              </Button>
            </motion.div>

            {/* Floating Date Badge */}
            <motion.div
              className="absolute -bottom-8 left-0"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1, duration: 0.4 }}
            >
              <StickerBadge rotate={-4} className="floating" testId="hero-badge-tonight">
                TONIGHT • 10PM
              </StickerBadge>
            </motion.div>
          </div>

          {/* Right: Editorial Image with Dramatic Treatment */}
          <div className="lg:col-span-5 relative">
            <motion.div
              className="relative editorial-frame rounded-3xl overflow-hidden border-2 border-[var(--accent-crimson)]/30 shadow-[0_0_60px_rgba(220,20,60,0.25)]"
              style={{ aspectRatio: '3/4', minHeight: '550px' }}
              initial={{ opacity: 0, scale: 0.92, rotateY: 5 }}
              animate={{ opacity: 1, scale: 1, rotateY: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              whileHover={{ scale: 1.02 }}
            >
              <img 
                src="/images/gallery-2.webp"
                alt="Club atmosphere"
                className="w-full h-full object-cover"
              />
              
              {/* Overlapping Elements */}
              <div className="absolute top-4 right-4 z-10">
                <StickerBadge className="bg-[var(--accent-crimson)] text-white border-none" testId="hero-image-badge">
                  LIVE
                </StickerBadge>
              </div>
            </motion.div>

            {/* Floating "21+" Badge */}
            <motion.div
              className="absolute -bottom-6 -right-4 z-20 hidden lg:block"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.2, duration: 0.4 }}
            >
              <StickerBadge rotate={8} className="floating bg-[var(--accent-gold)] text-black border-none" testId="hero-badge-21plus">
                21+ ONLY
              </StickerBadge>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.section>
  );
}

// ─── Blog Teaser Section ─────────────────────────────────────────────────────
// Renders 3 featured blog posts as crawlable <Link> cards above FinalCTA.
function BlogTeaserSection() {
  const featured = _blogPostsData.filter((p) => p.featured).slice(0, 3);
  if (featured.length === 0) return null;
  return (
    <motion.section
      data-testid="home-blog-teaser-section"
      className="py-20 md:py-24 bg-[var(--surface-2)] border-t border-[var(--border)]"
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.55 }}
    >
      <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#9A9A92] mb-2">
              Insights &amp; Guides
            </div>
            <h2 className="font-bebas text-3xl md:text-4xl tracking-tight text-white">
              From the Scratch Journal
            </h2>
            <p className="text-sm md:text-base text-[var(--text-dim)] mt-2 max-w-[60ch]">
              Curated reads on Jamshedpur&rsquo;s nightlife — from the best DJ nights to the city&rsquo;s top venues.
            </p>
          </div>
          <Link
            to="/blog"
            data-testid="home-blog-teaser-view-all-link"
            className="inline-flex items-center gap-1.5 text-sm text-[var(--accent-gold)] hover:text-[#E8B85A] self-start md:self-end"
          >
            View all articles <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {featured.map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              data-testid={`home-blog-card-${post.slug}`}
              className="group block rounded-2xl overflow-hidden bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--accent-gold)]/40 transition-all duration-200"
            >
              <div
                className="aspect-[16/10] bg-cover bg-center"
                style={{ backgroundImage: `url(${post.image})` }}
                role="img"
                aria-label={post.title}
              />
              <div className="p-5">
                <Badge className="mb-3 text-[10px] tracking-wider uppercase bg-white/5 border-white/10 text-[var(--text-dim)]">
                  {post.category}
                </Badge>
                <h3 className="font-bebas text-xl text-white leading-tight group-hover:text-[var(--accent-gold)] transition-colors mb-2">
                  {post.title}
                </h3>
                <p className="text-sm text-[var(--text-dim)] leading-relaxed line-clamp-3">
                  {post.excerpt}
                </p>
                <div className="text-xs text-[var(--text-muted)] mt-4">
                  {new Date(post.publishDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · {post.readTime}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </motion.section>
  );
}

